import { type ContextData } from '@common/defaults/ContextData';
import { isClientSide } from '@common/utils/isClientSide';

interface CacheConfig {
    headers: Record<string, string>;
    next: NonNullable<RequestInit['next']>;
}

interface CacheConfigContextData {
    cacheBuster?: ContextData['cacheBuster'];
    env?: ContextData['env'];
}

interface CacheConfigFetchOptions {
    next?: RequestInit['next'];
}

const NO_REVALIDATE_MAGIC_NUMBER = 321;

/**
 * Returns a cache buster header to prevent caching requests in Playwright tests.
 * We use a header instead of setting cache to "no-store" because Next.js
 * does not allow switching pages between cacheable and non-cacheable.
 */
export const getCacheConfig = (
    contextData: CacheConfigContextData,
    fetchOptions: CacheConfigFetchOptions | undefined,
): CacheConfig => {
    const cacheConfig: CacheConfig = {
        next: { ...fetchOptions?.next },
        headers: {},
    };

    // When running on the server with the redis cache handler enabled we set a magic number
    // if no revalidate has been set to get request deduplication. See `web/cache/cache-handler.mjs`.
    if (
        !isClientSide &&
        typeof cacheConfig.next.revalidate === 'undefined' &&
        process.env.REDIS_HOST &&
        process.env.REDIS_CACHE_HANDLER_ENABLED !== 'false'
    ) {
        cacheConfig.next.revalidate = NO_REVALIDATE_MAGIC_NUMBER;
    }

    if (contextData.cacheBuster) {
        // Set cache buster header to make sure this request is unqiue
        cacheConfig.headers['x-pxr-cache-buster'] = contextData.cacheBuster;

        // Reduce revalidation time if the request is usually being cached
        if (typeof cacheConfig.next.revalidate === 'number' && cacheConfig.next.revalidate > 1) {
            cacheConfig.next.revalidate = 1;
        }
    }

    return cacheConfig;
};
